import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { TurnstileWidget } from "@/components/mera/TurnstileWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ECOSYSTEM_LINKS = [
  { name: "Ryan", pillar: "Memory" },
  { name: "Fol.io", pillar: "Commerce" },
  { name: "EULR", pillar: "Commerce" },
  { name: "Cortex", pillar: "Intelligence" },
  { name: "PTRN", pillar: "Intelligence" },
  { name: "TRNSLT", pillar: "Intelligence" },
  { name: "KOVA", pillar: "Governance" },
  { name: "Railwright", pillar: "Governance" },
  { name: "Uday", pillar: "Mastery" },
  { name: "Vernai", pillar: "Mastery" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [widgetKey, setWidgetKey] = useState(0);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email,
        turnstile_token: turnstileToken || "",
      });
      toast.success("Subscribed. The next dispatch is on its way.");
      setEmail("");
      setTurnstileToken(null);
      setWidgetKey((k) => k + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many requests. Please try again in a minute.");
      } else if (status === 400) {
        toast.error(
          err?.response?.data?.detail || "Verification failed. Please try again."
        );
        setTurnstileToken(null);
        setWidgetKey((k) => k + 1);
      } else {
        toast.error(
          err?.response?.data?.detail?.[0]?.msg || "Could not subscribe."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A0A0A] text-[#F7F7F6] px-6 md:px-10 pt-20 pb-10"
    >
      {/* Dispatch */}
      <div className="grid grid-cols-12 gap-10 pb-16 border-b border-white/10">
        <div className="col-span-12 md:col-span-5">
          <div className="font-serif-display text-[44px] md:text-[64px] leading-[1.02] tracking-tight">
            The Dispatch.
          </div>
          <p className="mt-4 max-w-[460px] text-white/65 leading-relaxed">
            A monthly note from inside the ecosystem — what we shipped, what we
            learned, what we're still arguing about over coffee. Slow,
            unfiltered, and addressed to one human at a time.
          </p>
        </div>

        <div className="col-span-12 md:col-span-7 flex items-end">
          <form
            data-testid="newsletter-form"
            onSubmit={onSubscribe}
            className="w-full"
          >
            <label className="font-mono-label text-[10px] text-white/55 block mb-3">
              Subscribe
            </label>
            <TurnstileWidget
              key={widgetKey}
              onToken={(t) => setTurnstileToken(t)}
              onError={(m) => {
                setTurnstileToken(null);
                if (m) toast.error(m);
              }}
            />
            <div className="mt-4 flex items-center gap-3 border-b border-white/30 pb-2">
              <input
                data-testid="newsletter-input-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.io"
                className="flex-1 bg-transparent border-0 outline-none text-white placeholder:text-white/40 text-[18px] py-2"
                required
              />
              <button
                type="submit"
                disabled={loading || turnstileToken === null}
                data-testid="newsletter-submit"
                className="font-mono-label text-[11px] text-white/80 hover:text-white inline-flex items-center gap-2 transition-colors disabled:opacity-50"
                title={turnstileToken === null ? "Verifying you're human…" : undefined}
              >
                {loading
                  ? "Sending…"
                  : turnstileToken === null
                  ? "Verifying…"
                  : "Subscribe"}
                <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </div>
            <div className="font-mono-label text-[10px] text-white/40 mt-3">
              Once a month. Unsubscribe at the bottom of any note.
            </div>
          </form>
        </div>
      </div>

      {/* Sitemap */}
      <div className="pt-12 grid grid-cols-12 gap-8">
        {/* Brand */}
        <div className="col-span-12 md:col-span-4">
          <div className="font-serif-display text-[36px] leading-none">MERA</div>
          <div className="mt-1 font-mono-label text-[10px] text-white/45">
            The Human Ecosystem
          </div>
          <p className="mt-5 text-white/60 text-[14px] leading-relaxed max-w-[340px]">
            Ten AI-native products. One architecture. One philosophy — that
            technology should serve the human, not the other way around.
          </p>
          <div className="mt-6 font-mono-label text-[10px] text-white/45">
            meratechnology.com
          </div>
        </div>

        {/* Ecosystem (2 sub-columns) */}
        <div className="col-span-12 md:col-span-4">
          <div className="font-mono-label text-[10px] text-white/45 mb-3">
            The Ecosystem · 10
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[14px]">
            {ECOSYSTEM_LINKS.map((p) => (
              <a
                key={p.name}
                href="#foundry"
                className="text-white/75 hover:text-white transition-colors flex items-baseline justify-between gap-2"
              >
                <span>{p.name}</span>
                <span className="font-mono-label text-[9px] text-white/35">
                  {p.pillar}
                </span>
              </a>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <a
              href="#matr"
              className="font-mono-label text-[10px] text-white/65 hover:text-white inline-flex items-center gap-2"
            >
              ◦ MATR · The Intelligence Layer →
            </a>
          </div>
        </div>

        {/* Company */}
        <div className="col-span-6 md:col-span-2">
          <div className="font-mono-label text-[10px] text-white/45 mb-3">Company</div>
          <ul className="space-y-2 text-[14px]">
            <li><a href="#ecosystem" className="text-white/75 hover:text-white">Manifesto</a></li>
            <li><a href="#matr" className="text-white/75 hover:text-white">MATR</a></li>
            <li><a href="#founders" className="text-white/75 hover:text-white">The Steward</a></li>
            <li><a href="#press" className="text-white/75 hover:text-white">Press</a></li>
            <li><a href="#waitlist" className="text-white/75 hover:text-white">Waitlist</a></li>
          </ul>
        </div>

        {/* Reach */}
        <div className="col-span-6 md:col-span-2">
          <div className="font-mono-label text-[10px] text-white/45 mb-3">Reach us</div>
          <ul className="space-y-2 text-[14px]">
            <li>
              <a
                href="mailto:hello@meratechnology.com"
                className="text-white/75 hover:text-white break-all"
              >
                hello@meratechnology.com
              </a>
            </li>
            <li className="text-white/55">We reply within 48 hours.</li>
            <li className="text-white/55">Zero spam, by principle.</li>
          </ul>
        </div>
      </div>

      {/* Bottom rule */}
      <div className="mt-14 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono-label text-[10px] text-white/40">
          © {new Date().getFullYear()} MERA Technology · Edition 01
        </div>
        <div className="font-mono-label text-[10px] text-white/40">
          Built quietly · made to last
        </div>
      </div>
    </footer>
  );
};
